import { TextButton } from '@creditornot/cb-button';
import { colors, cbSemanticCssVars, Typography as T } from '@creditornot/cb-ingredients';
import React from 'react';
import { Field } from 'react-final-form';
import { FieldArray, useFieldArray } from 'react-final-form-arrays';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { KybSubmissionDocument } from 'apis/legitimizer/types';
import { RFFFieldErrorWrapper } from 'common/components';
import { useMerchantContext } from 'modules/merchant/MerchantContext';

import { DocumentUploader, DocumentViewer } from '../documents';
import { DocumentsFormFieldsConfig, DocumentUpload } from '../types';
import { createEmptyDocumentUpload } from '../utils';

export const StyledFieldset = styled.fieldset`
  padding: 0;
  border: 0;
  margin: 0;
  min-width: 0;
`;
const Divider = styled.div`
  height: 1px;
  background-color: ${cbSemanticCssVars.borderPrimary};
`;

const DocumentWrapper = styled.div`
  padding-block-start: 24px;
  padding-block-end: 24px;
`;

const DocumentsContainer = styled.div`
  & > ${DocumentWrapper}:not(:first-child) {
    border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  }
`;

const DecorativeNumberedList = styled.ol`
  padding-inline: 0;
  margin: 0;
  list-style-position: inside;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-block-end: 12px;
`;

const DecorativeLabel = styled(T.Body3)`
  color: ${cbSemanticCssVars.textSecondary};
  display: block;
  margin-block-start: 24px;
`;

const VisuallyHiddenLabel = styled.label`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
`;

const Asterisk = styled.span`
  color: ${colors.strawberry100};
`;

const AddButton = styled(TextButton).attrs({ variant: 'blue' })`
  margin-block-start: 8px;
  margin-block-end: 16px;
`;

const defaultDocumentsIdKeys = ['compliance.kyb.sections.documents.trade-registry-extract'];
const countryMapDocumentsIdKeys = {
  ALB: defaultDocumentsIdKeys,
  AUT: defaultDocumentsIdKeys,
  AZE: defaultDocumentsIdKeys,
  CYP: defaultDocumentsIdKeys,
  CZE: defaultDocumentsIdKeys,
  DEU: defaultDocumentsIdKeys,
  DNK: defaultDocumentsIdKeys,
  EST: defaultDocumentsIdKeys,
  FIN: defaultDocumentsIdKeys,
  GEO: defaultDocumentsIdKeys,
  GRC: defaultDocumentsIdKeys,
  HRV: defaultDocumentsIdKeys,
  HUN: defaultDocumentsIdKeys,
  ISL: defaultDocumentsIdKeys,
  ISR: [
    'compliance.kyb.sections.documents.isr.certificate-of-incorporation',
    'compliance.kyb.sections.documents.isr.withholding-tax-certificate',
    'compliance.kyb.sections.documents.isr.bookkeeping-authorization',
    'compliance.kyb.sections.documents.isr.protocol-of-authorized-signatories',
  ],
  JPN: defaultDocumentsIdKeys,
  KAZ: defaultDocumentsIdKeys,
  LTU: defaultDocumentsIdKeys,
  LUX: defaultDocumentsIdKeys,
  LVA: defaultDocumentsIdKeys,
  MLT: [
    'compliance.kyb.sections.documents.mlt.business-registry-certificate',
    'compliance.kyb.sections.documents.mlt.mta-license',
    'compliance.kyb.sections.documents.mlt.vat-certificate',
  ],
  NOR: defaultDocumentsIdKeys,
  POL: defaultDocumentsIdKeys,
  SRB: defaultDocumentsIdKeys,
  SVK: defaultDocumentsIdKeys,
  SVN: defaultDocumentsIdKeys,
  SWE: defaultDocumentsIdKeys,
  UZB: defaultDocumentsIdKeys,
};

type Props = {
  config: {
    documents: DocumentsFormFieldsConfig;
  };
  existingDocuments: KybSubmissionDocument[];
  isDocumentOptionalForSoleTrader: boolean;
  setDocumentIdList: React.Dispatch<React.SetStateAction<number[]>>;
};

const DocumentFormFields = ({
  config,
  existingDocuments,
  isDocumentOptionalForSoleTrader,
  setDocumentIdList,
}: Props) => {
  const registryExtractDocumentsFieldArray = useFieldArray('registryExtractDocuments');

  const newDocumentsLength = registryExtractDocumentsFieldArray.fields.length || 0;
  const singleExistingDocumentInView = existingDocuments.length === 1 && newDocumentsLength === 0;
  const singleNewDocumentInView = newDocumentsLength === 1 && existingDocuments.length === 0;

  const handleRemoveNewDocument = (index: number) => {
    if (singleNewDocumentInView) {
      registryExtractDocumentsFieldArray.fields.push(createEmptyDocumentUpload());
    }

    registryExtractDocumentsFieldArray.fields.remove(index);
  };

  const handleRemoveExistingDocument = (removedDocumentId: number) => {
    if (singleExistingDocumentInView) {
      registryExtractDocumentsFieldArray.fields.push(createEmptyDocumentUpload());
    }

    setDocumentIdList(documentIdList => {
      const updatedDocumentIdList = documentIdList.filter(documentId => documentId !== removedDocumentId);

      return updatedDocumentIdList;
    });
  };

  const { merchant } = useMerchantContext();

  const displayRequiredDocuments = () => {
    if (isDocumentOptionalForSoleTrader) {
      return displayRequiredDocument('compliance.kyb.sections.documents.supporting-documents');
    }

    const documentsIdKeys = countryMapDocumentsIdKeys[merchant.country] || defaultDocumentsIdKeys;
    return documentsIdKeys.length === 1
      ? displayRequiredDocument(documentsIdKeys[0])
      : displayRequiredDocumentsAsEnumeratedList(documentsIdKeys);
  };

  const displayRequiredDocumentsAsEnumeratedList = (idKeys: string[]) => (
    <DecorativeNumberedList>
      {idKeys.map(idKey => (
        <li>
          <FormattedMessage id={idKey} />
        </li>
      ))}
    </DecorativeNumberedList>
  );

  const isDocumentRequired = !isDocumentOptionalForSoleTrader && config.documents.registryExtractDocuments.required;

  const displayRequiredDocument = (idKey: string) => (
    <DecorativeLabel>
      <FormattedMessage id={idKey} /> {isDocumentRequired && <Asterisk>*</Asterisk>}
    </DecorativeLabel>
  );

  return (
    <StyledFieldset>
      {config.documents.registryExtractDocuments.visible && (
        <>
          <DecorativeLabel>{displayRequiredDocuments()}</DecorativeLabel>

          <DocumentsContainer>
            {existingDocuments.map(document => (
              <React.Fragment key={document.id}>
                <DocumentViewer document={document} onRemove={handleRemoveExistingDocument} />
              </React.Fragment>
            ))}

            <FieldArray<DocumentUpload> name="registryExtractDocuments">
              {({ fields }) => (
                <>
                  {fields.map((name: string, index: number) => (
                    <Field<File> name={`${name}.file`} key={fields.value[index].tempId}>
                      {field => (
                        <DocumentWrapper>
                          <RFFFieldErrorWrapper field={field}>
                            {input => (
                              <DocumentUploader
                                label={
                                  <VisuallyHiddenLabel htmlFor={`${name}.file`}>
                                    <FormattedMessage id="compliance.kyb.sections.documents.trade-registry-extract" />{' '}
                                    {isDocumentRequired && <Asterisk>*</Asterisk>}
                                  </VisuallyHiddenLabel>
                                }
                                inputProps={{
                                  id: `${name}.file`,
                                  name: `${name}.file`,
                                }}
                                onChange={input.onChange}
                                onRemove={() => handleRemoveNewDocument(index)}
                                hideRemove={!input.value && singleNewDocumentInView}
                              />
                            )}
                          </RFFFieldErrorWrapper>
                        </DocumentWrapper>
                      )}
                    </Field>
                  ))}
                </>
              )}
            </FieldArray>
          </DocumentsContainer>
        </>
      )}

      <AddButton onClick={() => registryExtractDocumentsFieldArray.fields.push(createEmptyDocumentUpload())}>
        <FormattedMessage id="compliance.kyb.sections.documents.add-document" />
      </AddButton>

      <Divider />
    </StyledFieldset>
  );
};

export default DocumentFormFields;
